import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import ArtistBetaBanner from '../components/ArtistBetaBanner';
import IntentModal from '../components/IntentModal';
import { 
  Mic2, 
  Upload, 
  Music, 
  DollarSign, 
  Users, 
  TrendingUp,
  Zap,
  MessageCircle,
  Radio,
  Award,
  CheckCircle,
  HelpCircle
} from 'lucide-react';
import axios from 'axios';
import { trackEvent } from '../utils/analytics';

const BACKEND_URL = '';  // Use relative URLs with proxy

const Landing = () => {
  const navigate = useNavigate();
  
  // Stats state with fallback values
  const [stats, setStats] = useState(null); // Start with null to indicate loading
  const [, setStatsLoaded] = useState(false);
  
  // Fallback stats used when API fails
  const fallbackStats = {
    totalReviews: 123,
    activeReviewers: 3,
    creatorEarningsUsd: 625,
    averageRating: 5.0
  };
  
  // Get display stats (actual or fallback)
  const displayStats = stats || fallbackStats;

  // Fetch live stats from backend
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Add cache-busting headers to prevent stale data
        const response = await axios.get(`${BACKEND_URL}/api/stats/live`, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          params: {
            _t: Date.now() // Cache-busting query param
          }
        });
        if (response.data) {
          setStats({
            totalReviews: response.data.total_reviews || fallbackStats.totalReviews,
            activeReviewers: response.data.active_reviewers || fallbackStats.activeReviewers,
            creatorEarningsUsd: Math.floor(response.data.creator_earnings) || fallbackStats.creatorEarningsUsd,
            averageRating: response.data.average_rating || fallbackStats.averageRating
          });
        }
        setStatsLoaded(true);
      } catch (error) {
        console.log('Using fallback stats:', error.message);
        setStats(fallbackStats);
        setStatsLoaded(true);
      }
    };
    
    fetchStats();
    // Refresh stats every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [showTwitchEmbed, setShowTwitchEmbed] = useState(false);

  useEffect(() => {
    // Only load Twitch embed when user clicks to show it
    if (!showTwitchEmbed) return;

    const script = document.createElement('script');
    script.src = 'https://player.twitch.tv/js/embed/v1.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.Twitch && !document.getElementById('twitch-embed-instance')) {
        // Get the actual hostname
        const hostname = window.location.hostname;
        console.log('Loading Twitch embed with parent:', hostname);
        
        const player = new window.Twitch.Player('homepage-twitch-embed', {
          channel: 'jxhnjacxb',
          parent: [hostname],
          width: '100%',
          height: 400,
          autoplay: false,  // Disable autoplay to avoid errors
          muted: false,
          layout: 'video',
          allowfullscreen: true
        });
        
        player.addEventListener(window.Twitch.Player.READY, () => {
          console.log('✅ Twitch embed loaded for jxhnjacxb');
          console.log('🎮 Click the player to start watching!');
        });
        player.addEventListener(window.Twitch.Player.OFFLINE, () => {
          console.log('📺 Channel is offline');
        });
        player.addEventListener(window.Twitch.Player.ONLINE, () => {
          console.log('🔴 Channel is LIVE!');
        });
      }
    };

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [showTwitchEmbed]);

  const features = [
    {
      icon: <Upload className="h-6 w-6" />,
      title: "Easy Uploads",
      description: "Upload MP3/WAV or paste links fast—no messy email chains."
    },
    {
      icon: <Radio className="h-6 w-6" />,
      title: "Live Review Sessions",
      description: "Build real relationships with artists and fans through live feedback."
    },
    {
      icon: <DollarSign className="h-6 w-6" />,
      title: "Tip + Support",
      description: "Get paid with skips and tips that are front-and-center—no confusion."
    },
    {
      icon: <MessageCircle className="h-6 w-6" />,
      title: "Millionaire Café",
      description: "On-platform forum to trade feedback, find collabs, and level up fast."
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "Reviewer Growth Dashboard",
      description: "Track submissions, payouts, and performance so you can improve weekly."
    },
    {
      icon: <Award className="h-6 w-6" />,
      title: "Smart Queue System",
      description: "Auto-sorts priority submissions with color-coded badges and reviewer alerts."
    }
  ];

  const benefits = [
    "Smart Queue that auto-prioritizes paid skips",
    "Skip • Super Skip • Custom Tip options (clearly labeled)",
    "Built-in community forum (Millionaire Café)",
    "Mobile-optimized experience",
    "Go-live notifications + follower capture (SMS/email)",
    "Creator-first moderation (positivity, not trashing)"
  ];

  const faqs = [
    {
      question: "Do I need a big following to become a reviewer?",
      answer: "No. Mic Check Millionaire is built for beginners and small creators. If you can stay consistent, you can grow and earn."
    },
    {
      question: "How do reviewers make money?",
      answer: "Reviewers earn through tips, Skip, Super Skip, and Custom Tips during live sessions."
    },
    {
      question: "What makes the Smart Queue different?",
      answer: "Paid skips auto-prioritize instantly with clear badges and reviewer notifications—so you never miss paid support."
    },
    {
      question: "Is Mic Check Millionaire positivity-first?",
      answer: "Yes. Reviews are built to encourage artists and give actionable feedback without trashing or disrespect."
    },
    {
      question: "Do I have to leave the site to build community?",
      answer: "No. The Millionaire Café is built into the platform so creators and artists can connect in one place."
    },
    {
      question: "What are the monetization options for reviewers?",
      answer: "Free plan: Mic Check Millionaire takes 10% of tips. Pro option: $10/month to keep 100% of tips via Cash App."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* High-Intent Conversion Modal */}
      <IntentModal />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-gray-50 to-white pt-8 sm:pt-16 pb-12 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-[#F2D857] text-black hover:bg-[#F2D857]/90 text-sm px-4 py-1">
              <Zap className="h-3 w-3 mr-1 inline" />
              Beginner-Friendly • Positivity-First • Built to Monetize
            </Badge>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-montserrat mb-4 tracking-tight px-4">
              Turn Music Reviews Into <span className="text-[#F2D857]">Consistent Income.</span>
            </h1>
            
            {/* Identity-based qualifier - A */}
            <p className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 px-4">
              Built for new and rising reviewers who want to get paid without a big following.
            </p>
            
            {/* Credibility anchor - D */}
            <div className="flex justify-center mb-6">
              <span className="inline-flex items-center text-sm sm:text-base font-medium text-gray-700 bg-gray-100 px-4 py-2 rounded-full">
                <DollarSign className="h-4 w-4 mr-1 text-green-600" />
                <span className="text-green-600 font-bold">${displayStats.creatorEarningsUsd}+</span>
                <span className="ml-1">paid out to reviewers so far</span>
              </span>
            </div>

            {/* 2-option decision strip - B */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-6 mb-8 px-4">
              <div className="flex items-center text-gray-600 text-sm sm:text-base">
                <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                I&apos;m here to submit music
                <span className="ml-2 text-gray-400">↓</span>
              </div>
              <div className="hidden sm:block w-px h-6 bg-gray-300"></div>
              <div className="flex items-center text-gray-900 font-semibold text-sm sm:text-base">
                <span className="w-2 h-2 bg-[#F2D857] rounded-full mr-2"></span>
                I want to review music and get paid
                <span className="ml-2 text-[#F2D857]">↓</span>
              </div>
            </div>

            <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed px-4">
              Mic Check Millionaire is the beginner-friendly, positivity-first platform where independent artists and new reviewers grow together. Host live reviews, build a fanbase, and get paid with a smarter queue and built-in community—no superstar status required.
            </p>
            
            {/* Reordered CTAs - C: Primary = Become a Reviewer, Secondary = Submit Music */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 max-w-md sm:max-w-none mx-auto">
              <Button
                size="lg"
                onClick={() => {
                  trackEvent('reviewer_cta_click');
                  navigate('/signup?type=reviewer');
                }}
                className="bg-black hover:bg-gray-800 text-white text-sm sm:text-lg px-4 sm:px-8 py-3 sm:py-6 transition-all w-full sm:w-auto font-bold shadow-lg"
                data-testid="hero-become-reviewer-btn"
              >
                <Mic2 className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Become a Reviewer — Free
              </Button>
              <Button
                size="lg"
                onClick={() => {
                  trackEvent('artist_cta_click');
                  navigate('/upload');
                }}
                className="bg-[#F2D857] hover:bg-[#F2D857]/90 text-black text-sm sm:text-lg px-4 sm:px-8 py-3 sm:py-6 transition-all w-full sm:w-auto"
                data-testid="hero-submit-music-btn"
              >
                <Upload className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Submit Music
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/reviewers')}
                className="border-2 border-gray-300 text-gray-700 hover:bg-gray-100 text-sm sm:text-lg px-4 sm:px-8 py-3 sm:py-6 transition-all w-full sm:w-auto"
                data-testid="hero-find-reviewer-btn"
              >
                <Users className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Find a Reviewer
              </Button>
            </div>
          </div>

          {/* Stats - Connected to Backend for Real-Time Updates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-12 sm:mt-16">
            <div className="text-center p-4 sm:p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-2xl sm:text-3xl font-bold text-black mb-1">{displayStats.totalReviews}+</div>
              <div className="text-xs sm:text-sm text-gray-600">Total Reviews</div>
            </div>
            <div className="text-center p-4 sm:p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-2xl sm:text-3xl font-bold text-black mb-1">{displayStats.activeReviewers}+</div>
              <div className="text-xs sm:text-sm text-gray-600">Active Reviewers</div>
            </div>
            <div className="text-center p-4 sm:p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-2xl sm:text-3xl font-bold text-black mb-1">${displayStats.creatorEarningsUsd}+</div>
              <div className="text-xs sm:text-sm text-gray-600">Creator Earnings</div>
            </div>
            <div className="text-center p-4 sm:p-6 bg-white rounded-xl shadow-sm border border-gray-100">
              <div className="text-2xl sm:text-3xl font-bold text-black mb-1">{displayStats.averageRating}</div>
              <div className="text-xs sm:text-sm text-gray-600">Average Rating</div>
            </div>
          </div>
          <p className="text-center text-sm text-gray-500 mt-4 mb-2 sm:mb-0">
            Real sessions. Real creators. Real payouts — built from the ground up by active reviewers.
          </p>
        </div>
      </section>

      {/* Artist Beta Banner */}
      <section className="py-4 sm:py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ArtistBetaBanner />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 px-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold font-montserrat mb-4">Why Choose Mic Check Millionaire?</h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Most platforms reward the biggest names. Mic Check Millionaire is built for the little guy—beginners, small creators, and independent artists who want structure, positive feedback, and real income.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="p-6 border-2 border-gray-100 hover:border-[#F2D857] hover:shadow-lg transition-all group">
                <div className="bg-black group-hover:bg-[#F2D857] text-[#F2D857] group-hover:text-black p-3 rounded-lg w-fit mb-4 transition-all">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Live Reviewer Showcase */}
      <section className="py-10 sm:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold font-montserrat mb-4">
              Live Reviewer <span className="text-[#F2D857]">Showcase</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Watch real reviews, see the queue in action, and learn the flow before your first show.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Live Stream or Offline Placeholder */}
            <Card className="overflow-hidden border-2 border-gray-200 shadow-2xl mb-8">
              <div className="bg-black p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-[#F2D857] flex items-center justify-center">
                      <Music className="h-6 w-6 text-black" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg">JxhnJacxb&apos;s Stream</h3>
                      <p className="text-purple-100 text-sm">Live music reviews &amp; artist feedback sessions</p>
                    </div>
                  </div>
                </div>
              </div>
              {!showTwitchEmbed ? (
                <div 
                  className="w-full bg-gradient-to-br from-purple-900 to-black flex items-center justify-center cursor-pointer hover:from-purple-800 transition-colors"
                  style={{ minHeight: '400px', height: '400px' }}
                  onClick={() => setShowTwitchEmbed(true)}
                >
                  <div className="text-center">
                    <div className="w-20 h-20 bg-[#F2D857] rounded-full flex items-center justify-center mx-auto mb-4 hover:scale-110 transition-transform">
                      <svg className="w-10 h-10 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                    <p className="text-white font-bold text-lg">Watch Live Sessions</p>
                    <p className="text-purple-200 text-sm mt-1">Click to load Twitch stream</p>
                  </div>
                </div>
              ) : (
                <div 
                  id="homepage-twitch-embed" 
                  className="w-full bg-black" 
                  style={{
                    minHeight: '400px',
                    height: '400px',
                    visibility: 'visible',
                    display: 'block'
                  }}
                ></div>
              )}
            </Card>

            {/* This Could Be You - Reviewer Showcase */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold font-montserrat mb-2">
                This Could Be <span className="text-[#F2D857]">You</span>
              </h3>
              <p className="text-gray-600">Join our community of professional music reviewers</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Male Reviewer Card */}
              <Card 
                className="overflow-hidden border-2 border-gray-200 hover:border-[#F2D857] hover:shadow-2xl transition-all cursor-pointer group"
              >
                <div className="relative h-64 bg-gradient-to-br from-gray-800 to-gray-900">
                  <img
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop"
                    alt="Aspiring male music reviewer ready to host live feedback sessions on Mic Check Millionaire"
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <Badge className="bg-[#F2D857] text-black mb-3">Featured Reviewer</Badge>
                    <h4 className="text-2xl font-bold mb-1">Your Name Here</h4>
                    <p className="text-gray-300 text-sm mb-3">Hip-Hop • R&B • Pop Specialist</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        Growing Audience
                      </span>
                      <span className="flex items-center">
                        <DollarSign className="h-4 w-4 mr-1" />
                        Earning Daily
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-white">
                  <p className="text-gray-600 mb-4">
                    &quot;I turned my music knowledge into a full-time income. You can too.&quot;
                  </p>
                  <a
                    href="/api/analytics/book-click?source=landing_page"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="w-full bg-black hover:bg-[#F2D857] hover:text-black text-white group-hover:scale-105 transition-all">
                      <Mic2 className="mr-2 h-4 w-4" />
                      Start Your Journey
                    </Button>
                  </a>
                </div>
              </Card>

              {/* Female Reviewer Card */}
              <Card 
                className="overflow-hidden border-2 border-gray-200 hover:border-[#F2D857] hover:shadow-2xl transition-all cursor-pointer group"
              >
                <div className="relative h-64 bg-gradient-to-br from-purple-800 to-indigo-900">
                  <img
                    src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop"
                    alt="Aspiring female music reviewer building community through live review sessions"
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <Badge className="bg-[#F2D857] text-black mb-3">Featured Reviewer</Badge>
                    <h4 className="text-2xl font-bold mb-1">Your Name Here</h4>
                    <p className="text-gray-300 text-sm mb-3">Electronic • Indie • Alternative Expert</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        Building Community
                      </span>
                      <span className="flex items-center">
                        <TrendingUp className="h-4 w-4 mr-1" />
                        Growing Fast
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-white">
                  <p className="text-gray-600 mb-4">
                    &quot;Helping artists while monetizing my passion? Best decision ever.&quot;
                  </p>
                  <Button 
                    onClick={() => navigate('/become-reviewer')}
                    className="w-full bg-black hover:bg-[#F2D857] hover:text-black text-white group-hover:scale-105 transition-all"
                  >
                    <Mic2 className="mr-2 h-4 w-4" />
                    Become a Reviewer
                  </Button>
                </div>
              </Card>
            </div>

            <div className="text-center">
              <p className="text-gray-700 text-lg mb-6">
                💸 Join thousands of reviewers earning money to do what they love—listen to music 🎧
              </p>
              <Button
                onClick={() => navigate('/signup')}
                size="lg"
                className="bg-[#F2D857] hover:bg-[#F2D857]/90 text-black text-lg px-10 py-6 rounded-xl font-bold shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
              >
                <Mic2 className="mr-2 h-5 w-5" />
                Start Reviewing & Earning Today
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-10 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold font-montserrat mb-6">
                Built for the Modern Music Creator
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Whether you&apos;re an artist trying to get heard or a new reviewer trying to get paid, Mic Check Millionaire gives you a simple system: go live, run the queue, build community, and monetize every session.
              </p>
              <div className="grid grid-cols-1 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-[#F2D857] flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              <Button
                size="lg"
                onClick={() => navigate('/signup')}
                className="mt-8 bg-black hover:bg-[#F2D857] hover:text-black text-white text-lg px-8 py-6"
              >
                Get Started Free
              </Button>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-[#F2D857] to-[#F2D857]/60 rounded-2xl p-8 shadow-2xl">
                <div className="bg-white rounded-xl p-6 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="font-semibold">Music Review Session</div>
                    <Badge className="bg-green-100 text-green-700">Active</Badge>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-12 h-12 bg-black rounded-lg"></div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">Midnight Dreams</div>
                        <div className="text-xs text-gray-500">Jay Phoenix • 3:24</div>
                      </div>
                      <Badge className="bg-[#F2D857] text-black text-xs">Skip</Badge>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-12 h-12 bg-gray-300 rounded-lg"></div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">Lost in the City</div>
                        <div className="text-xs text-gray-500">Sarah Melodies • 4:12</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-black text-white rounded-xl p-6">
                  <div className="text-sm text-gray-400 mb-2">Session Earnings</div>
                  <div className="text-3xl font-bold">$247.50</div>
                  <div className="text-sm text-gray-400 mt-2">23 submissions today</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-10 sm:py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold font-montserrat mb-4">
              Frequently Asked <span className="text-[#F2D857]">Questions</span>
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about getting started.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="p-6 border-2 border-gray-100 hover:border-[#F2D857] transition-all">
                <div className="flex items-start gap-4">
                  <div className="bg-[#F2D857] p-2 rounded-lg flex-shrink-0">
                    <HelpCircle className="h-5 w-5 text-black" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{faq.question}</h3>
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold font-montserrat mb-6">
            Ready to Turn Your Voice Into Income?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join thousands of artists and reviewers building their music careers on Mic Check Millionaire.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/signup')}
              className="bg-[#F2D857] hover:bg-[#F2D857]/90 text-black text-lg px-8 py-6"
            >
              <Mic2 className="mr-2 h-5 w-5" />
              Start Your Session
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/cafe')}
              className="border-2 border-white text-white hover:bg-white hover:text-black text-lg px-8 py-6"
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Join the Café
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
